import type { Device } from 'src/types/Device';
import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';
import type { CameraRecordingEncryption, CameraStatus } from '../types/camera-status';
import { devices_status } from 'src/stores';
import { compareFWDates, getDeviceFirmwareString } from 'src/helpers/firmware_compare_helpers';
import { getCameraComponentKey } from './camera-helpers';

/** Minimum firmware build (YYYYMMDD-HHMMSS) that supports the `video.tint` setting. */
const VIDEO_TINT_MIN_FW = '20260601-205051';

/** Minimum firmware build (YYYYMMDD-HHMMSS) that supports the `video.temperature` setting. */
const VIDEO_TEMPERATURE_MIN_FW = '20260603';

/**
 * Whether the device firmware is at least `minFw`: it must be either the exact
 * introducing build or a build from a later date.
 */
const isMinFirmware = (device: Device, minFw: string): boolean => {
	const deviceId = real_id(device.id);
	const fwId = getDeviceFirmwareString(
		device,
		{ [deviceId]: readDeviceSettings(device.id) },
		{ [deviceId]: readDeviceStatus(device.id) },
	);
	if (!fwId) return false;

	return fwId.includes(minFw) || compareFWDates(fwId, '>', minFw.split('-')[0]);
};

/**
 * Whether the device firmware supports the camera `video.tint` setting.
 *
 * @ToDo: Remove checks after device released. This is for pre-release versions.
 */
export const isVideoTintSupported = (device: Device): boolean =>
	isMinFirmware(device, VIDEO_TINT_MIN_FW);

/**
 * Whether the device firmware supports the camera `video.temperature` (color
 * temperature) setting.
 *
 * @ToDo: Remove checks after device released. This is for pre-release versions.
 */
export const isVideoTemperatureSupported = (device: Device): boolean =>
	isMinFirmware(device, VIDEO_TEMPERATURE_MIN_FW);

/**
 * Reads `recording_encryption` from the device's `camera:{n}` status.
 *
 * @param device - The camera device.
 * @param deviceStatus - This device's status; looked up by real id when omitted.
 * Svelte callers pass `$devices_status[realId]` to stay reactive.
 * @returns The reported encryption state, or `undefined` on firmware without it.
 */
const getRecordingEncryptionStatus = (
	device: Device,
	deviceStatus: DeviceStatusG2 = devices_status.getByRealId(device?.id),
): CameraRecordingEncryption | undefined => {
	const cameraKey = getCameraComponentKey(device);
	const cameraStatus = deviceStatus?.[cameraKey] as CameraStatus | undefined;
	return cameraStatus?.recording_encryption;
};

/**
 * Whether the device firmware supports cloud recording encryption (creating and
 * rotating the recording master key) - such firmware reports `recording_encryption`.
 */
export const isCameraEncryptionSupported = (device: Device, deviceStatus?: DeviceStatusG2): boolean =>
	!!getRecordingEncryptionStatus(device, deviceStatus);

/** Whether the camera itself currently holds a recording master key. */
export const isCameraEncryptionConfigured = (device: Device, deviceStatus?: DeviceStatusG2): boolean =>
	getRecordingEncryptionStatus(device, deviceStatus)?.configured === true;
